"use client";

import { useMutation } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { ChevronLeft, Loader2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { useI18n } from "@/locales/client";
import { useTRPC } from "@/trpc/client";
import useFeedbackModal from "../hooks/use-feedback-modal";
import { StarRating } from "./star-rating";

interface FeedbackFormProps {
  onBack: () => void;
  onSuccess: () => void;
}

const CATEGORY_KEYS = [
  {
    value: "Feature Request",
    labelKey: "feedbackModal.feedbackForm.categories.featureRequest" as const,
  },
  {
    value: "UX / Design",
    labelKey: "feedbackModal.feedbackForm.categories.uxDesign" as const,
  },
  {
    value: "Content Quality",
    labelKey: "feedbackModal.feedbackForm.categories.contentQuality" as const,
  },
  {
    value: "Performance",
    labelKey: "feedbackModal.feedbackForm.categories.performance" as const,
  },
  {
    value: "Other",
    labelKey: "feedbackModal.feedbackForm.categories.other" as const,
  },
] as const;

type Category = (typeof CATEGORY_KEYS)[number]["value"];

export const FeedbackForm = ({ onBack, onSuccess }: FeedbackFormProps) => {
  const [category, setCategory] = useState<Category | "">("");
  const [body, setBody] = useState("");
  const [rating, setRating] = useState(0);
  const { canSubmitFeedback, recordFeedbackSubmission } = useFeedbackModal();
  const t = useI18n();

  const trpc = useTRPC();
  const submitFeedback = useMutation(
    trpc.feedback.submitFeedback.mutationOptions({
      onSuccess: () => {
        recordFeedbackSubmission();
        onSuccess();
      },
      onError: (error) => {
        toast.error(
          error.message || t("feedbackModal.feedbackForm.submitError"),
        );
      },
    }),
  );

  const handleSubmit = () => {
    if (!category) return;
    if (!canSubmitFeedback()) {
      toast.error(t("feedbackModal.feedbackForm.rateLimitError"));
      return;
    }
    submitFeedback.mutate({
      category,
      body: body.trim(),
      rating: rating > 0 ? rating : undefined,
      pageUrl: window.location.href,
    });
  };

  const canSubmit =
    body.trim().length > 0 && category !== "" && !submitFeedback.isPending;

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.15 }}
      className="flex flex-col gap-4"
    >
      {/* Category selector */}
      <div>
        <span className="mb-2 block text-[13px] font-medium text-foreground">
          {t("feedbackModal.feedbackForm.whatsThisAbout")}
        </span>
        <div className="flex flex-wrap gap-2">
          {CATEGORY_KEYS.map((cat) => (
            <button
              key={cat.value}
              type="button"
              onClick={() => setCategory(cat.value)}
              className={cn(
                "rounded-full border-[1.5px] px-3.5 py-1.5 text-[13px] font-medium transition-all cursor-pointer",
                category === cat.value
                  ? "border-[#0077FF] bg-[#0077FF]/10 text-[#0077FF]"
                  : "border-border text-muted-foreground hover:border-border/80",
              )}
            >
              {t(cat.labelKey)}
            </button>
          ))}
        </div>
      </div>

      {/* Body */}
      <div>
        <label
          htmlFor="feedback-body"
          className="mb-1.5 block text-[13px] font-medium text-foreground"
        >
          {t("feedbackModal.feedbackForm.tellUsMore")}{" "}
          <span className="text-red-500">*</span>
        </label>
        <Textarea
          id="feedback-body"
          placeholder={t("feedbackModal.feedbackForm.tellUsMorePlaceholder")}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          className="resize-vertical focus-visible:ring-[#0077FF]"
        />
      </div>

      {/* Star rating */}
      <div>
        <span className="mb-2 block text-[13px] font-medium text-foreground">
          {t("feedbackModal.feedbackForm.experienceLabel")}
        </span>
        <StarRating value={rating} onChange={setRating} />
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={onBack} className="gap-1">
          <ChevronLeft className="size-4" />
          {t("feedbackModal.back")}
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!canSubmit}
          className="bg-[#0077FF] hover:bg-[#0066DD] disabled:bg-muted disabled:text-muted-foreground"
        >
          {submitFeedback.isPending ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            t("feedbackModal.feedbackForm.submitButton")
          )}
        </Button>
      </div>
    </motion.div>
  );
};
